// src/pages/CourseDetailPage.tsx
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetCourseQuery } from "../services/courses";
import {
  ChevronLeft,
  PlayCircle,
  CheckCircle,
  Clock,
  FileText,
  BookOpen,
} from "lucide-react";

export default function CourseDetailPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { data: course, isLoading, error } = useGetCourseQuery(courseId!);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading course...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Course Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The course you're looking for doesn't exist or you don't have access
            to it.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Calculate course stats
  const totalLessons = (course.modules || []).reduce(
    (acc: number, mod: any) => acc + (mod.lessons || []).length,
    0
  );
  const completedLessons = (course.modules || []).reduce(
    (acc: number, mod: any) =>
      acc + (mod.lessons || []).filter((l: any) => l.completed).length,
    0
  );
  const courseProgress =
    totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ChevronLeft size={20} />
            <span>Back to Dashboard</span>
          </button>

          <div className="flex items-start gap-6">
            <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <BookOpen size={40} className="text-white" />
            </div>

            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {course.title}
              </h1>
              {course.description && (
                <p className="text-gray-600 text-lg mb-4">
                  {course.description}
                </p>
              )}

              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <BookOpen size={16} className="text-gray-500" />
                  <span className="text-gray-700">
                    {(course.modules || []).length} modules
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <PlayCircle size={16} className="text-gray-500" />
                  <span className="text-gray-700">{totalLessons} lessons</span>
                </div>
                {courseProgress > 0 && (
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-600" />
                    <span className="text-green-600 font-medium">
                      {courseProgress}% complete
                    </span>
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              {courseProgress > 0 && (
                <div className="mt-4 max-w-md">
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-teal-500 h-full transition-all duration-300"
                      style={{ width: `${courseProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Course Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {(course.modules || []).map((module: any, moduleIdx: number) => {
            const moduleLessons = module.lessons || [];
            const completedInModule = moduleLessons.filter(
              (l: any) => l.completed
            ).length;
            const moduleProgress =
              moduleLessons.length > 0
                ? Math.round((completedInModule / moduleLessons.length) * 100)
                : 0;

            return (
              <div
                key={module._id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                {/* Module Header */}
                <div className="px-6 py-5 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-gray-900 mb-1">
                        Module {moduleIdx + 1}: {module.title}
                      </h2>
                      {module.description && (
                        <p className="text-gray-600">{module.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
                        <span>{moduleLessons.length} lessons</span>
                        {moduleProgress > 0 && (
                          <span className="text-teal-600 font-medium">
                            {moduleProgress}% complete
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Module Progress Bar */}
                  {moduleProgress > 0 && (
                    <div className="mt-4">
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-teal-500 h-full transition-all duration-300"
                          style={{ width: `${moduleProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Lessons List */}
                <div className="divide-y divide-gray-200">
                  {moduleLessons.map((lesson: any, lessonIdx: number) => (
                    <div
                      key={lesson._id}
                      onClick={() =>
                        navigate(`/dashboard/lesson/${lesson._id}`)
                      }
                      className="px-6 py-5 hover:bg-gray-50 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-start gap-4">
                        {/* Lesson Number/Status */}
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-700 group-hover:bg-teal-100 group-hover:text-teal-700 transition-colors">
                          {lesson.completed ? (
                            <CheckCircle size={20} className="text-green-600" />
                          ) : (
                            <span>{lessonIdx + 1}</span>
                          )}
                        </div>

                        {/* Lesson Details */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors mb-1">
                            {lesson.title} 
                          </h3>
                          {lesson.description && (
                            <p
                              className="text-sm text-gray-600 mb-2 line-clamp-2"
                              dangerouslySetInnerHTML={{
                                __html: lesson.description,
                              }}
                            />
                          )}
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            {lesson.duration && (
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {Math.floor(lesson.duration / 60)} min
                              </span>
                            )}
                            {lesson.hasWorksheet && (
                              <span className="flex items-center gap-1">
                                <FileText size={12} />
                                Worksheet
                              </span>
                            )}
                            {lesson.completed && (
                              <span className="flex items-center gap-1 text-green-600 font-medium">
                                <CheckCircle size={12} />
                                Completed
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Play Button */}
                        <button className="flex-shrink-0 p-3 hover:bg-teal-50 rounded-full transition-colors">
                          <PlayCircle size={28} className="text-teal-600" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* No lessons message */}
                  {moduleLessons.length === 0 && (
                    <div className="px-6 py-8 text-center text-sm text-gray-500">
                      No lessons available in this module yet
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* No modules message */}
          {(!course.modules || course.modules.length === 0) && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <BookOpen size={48} className="text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Modules Yet
              </h3>
              <p className="text-gray-600">
                This course doesn't have any modules yet. Check back later!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
